import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { TopItemsData } from '@/entities/dashboard';

interface TopItemsListProps {
  data?: TopItemsData;
  loading?: boolean;
}

const SKELETON_ROWS = 5;

/**
 * A top-N share list the way legacy drew it: one row per item — name, a thin bar
 * of its share, the percentage. Hovering a row shows its full name and share, as
 * the bar chart it replaced did, so a name the row cuts can still be read.
 */
export function TopItemsList({ data, loading }: TopItemsListProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <ul className="space-y-3">
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <li key={index}>
            <Skeleton className="h-4 w-full" />
          </li>
        ))}
      </ul>
    );
  }

  if (!data?.series.length) {
    return <p className="py-4 text-center text-sm text-muted-foreground">{t('common.noData')}</p>;
  }

  return (
    <ul className="space-y-2">
      {data.series.map((item, index) => {
        const share = `${item.percentage.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

        return (
          <Tooltip key={`${item.name}-${index}`}>
            <TooltipTrigger asChild>
              <li className="grid cursor-default grid-cols-[minmax(0,2fr)_minmax(0,3fr)_3.5rem] items-center gap-3 text-sm">
                <span className="truncate">{item.name}</span>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(Math.max(item.percentage, 0), 100)}%`, backgroundColor: item.color }}
                  />
                </div>
                <span className="text-right tabular-nums text-muted-foreground">{share}</span>
              </li>
            </TooltipTrigger>
            <TooltipContent>
              {item.name}: <strong>{share}</strong>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </ul>
  );
}
